/**
 * Matching for the search boxes that filter a list already in the browser (the go-kart board, notifications, import
 * history, the choose-a-shop grid, long tick lists). Pages whose list lives on the server send `q` instead.
 *
 * A row matches when every word typed is found in one of its fields, ignoring case and accents. Spaces and punctuation
 * do not matter inside numbers and plates: "kl11ab" finds "KL 11 AB 1234" and "98765 43210" finds "+919876543210".
 */

type Field = string | number | null | undefined;

/** Lower case, accents dropped, runs of spaces collapsed. */
export function foldText(value: Field): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

const compact = (s: string) => s.replace(/[^a-z0-9]/g, '');

/** True when the query is empty or every word of it is found in the fields (see the module comment). */
export function matchesQuery(query: string, fields: Field[]): boolean {
  const q = foldText(query);
  if (!q) return true;
  const hay = fields.map(foldText).filter(Boolean);
  if (!hay.length) return false;
  const hayCompact = hay.map(compact);
  // The whole query without its spaces, for plates and mobile numbers typed in pieces.
  const whole = compact(q);
  if (whole.length >= 3 && hayCompact.some((h) => h.includes(whole))) return true;
  return q.split(' ').every((word) => {
    if (hay.some((h) => h.includes(word))) return true;
    const w = compact(word);
    return w.length > 0 && hayCompact.some((h) => h.includes(w));
  });
}

/** The rows of a list that match the query (the same array when the query is empty). */
export function filterByQuery<T>(rows: T[], query: string, fields: (row: T) => Field[]): T[] {
  if (!foldText(query)) return rows;
  return rows.filter((r) => matchesQuery(query, fields(r)));
}

/**
 * Search on a board of numbered tickets (go-kart tokens): a bare number of up to `digits` digits is the ticket number
 * and must match exactly ("12" is token 12, not every mobile with a 12 in it); anything else is matched as text.
 */
export function matchesTicket(query: string, ticketNo: number, fields: Field[], digits = 3): boolean {
  const q = query.trim();
  if (!q) return true;
  if (q.length <= digits && /^\d+$/.test(q)) return ticketNo === Number(q);
  return matchesQuery(q, fields);
}
